import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkCircle01Icon, Loading03Icon } from '@hugeicons/core-free-icons';
import { ModalPortal } from '../shared/ModalPortal';
import { useModalA11y } from '../../hooks/useModalA11y';
import type { SuccessDismissMethod } from '../../hooks/useSubscriptionActivation';

/** The success message closes itself after this long (Emily, 2026-10-18: "it should auto dismiss in 5 seconds"). */
export const SUCCESS_AUTO_DISMISS_MS = 5_000;

/** Shown in place of the plan card and chooser while a paid plan is confirmed. */
export function ActivationPending() {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus({ preventScroll: true }); }, []);
  return (
    <section className="card-sm mb-6" aria-label="Subscription activation" aria-busy="true">
      <div className="flex items-center gap-3">
        <HugeiconsIcon icon={Loading03Icon} size={20} color="currentColor" className="text-gray-500 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        <h3 ref={heading} tabIndex={-1} className="text-lg font-semibold text-gray-900 focus:outline-none">Updating your plan</h3>
      </div>
      <p role="status" className="mt-3 text-sm text-gray-600">Payment received. We’re confirming your plan and seats.</p>
    </section>
  );
}

/**
 * The success message itself, shared with the /design-system showcase. Colors are
 * .alert-success; size is the dashboard toast's (Sonner: 8px radius, 16px icon,
 * 356px cap from the wrapper). A focus target, not a control: WhatsNewModal's
 * tabIndex={-1} pattern, so no ring.
 */
export const SubscriptionSuccessCard = forwardRef<HTMLDivElement, { text: string }>(function SubscriptionSuccessCard({ text }, ref) {
  return (
    <div ref={ref} tabIndex={-1} data-subscription-success
      className="alert-success pointer-events-auto flex items-start gap-2 rounded-lg px-4 py-3 text-sm shadow-lg animate-slide-up motion-reduce:animate-none focus:outline-none">
      <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} strokeWidth={2} color="currentColor" className="mt-1 shrink-0 text-green-600" aria-hidden="true" />
      <p id="subscription-success-message" className="font-medium text-green-800 [text-wrap:pretty]">{text}</p>
    </div>
  );
});

/**
 * The confirmed-plan message: toast-sized, centered at eye level (22vh, 18vh on
 * phones) over the dashboard's black/50 modal backdrop, no X (Emily,
 * 2026-10-18). It closes itself after SUCCESS_AUTO_DISMISS_MS; Escape or a
 * click outside closes it sooner. Either way focus moves to the plan name.
 * ponytail: the timer does not pause on hover or a hidden tab, as the shared
 * toast does; add that if a 5 s read proves too short.
 */
export function SubscriptionSuccess({ planName, seats, returnFocusId, onDismiss }: {
  planName: string;
  seats: number;
  /** Element to focus once the message closes (the plan name). */
  returnFocusId: string;
  onDismiss: (method: SuccessDismissMethod) => void;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const message = useRef<HTMLDivElement>(null);
  const close = useCallback((method: SuccessDismissMethod) => {
    onDismiss(method);
    requestAnimationFrame(() => document.getElementById(returnFocusId)?.focus({ preventScroll: true }));
  }, [onDismiss, returnFocusId]);
  const closeOnEscape = useCallback(() => close('escape'), [close]);
  useModalA11y(true, dialog, closeOnEscape);
  useEffect(() => {
    const timer = setTimeout(() => close('auto'), SUCCESS_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [close]);
  useEffect(() => {
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    message.current?.focus({ preventScroll: true });
    return () => { document.body.style.overflow = overflow; };
  }, []);

  const text = Number.isFinite(seats)
    ? `Your ${planName} plan is now active with ${seats} ${seats === 1 ? 'seat' : 'seats'}.`
    : `Your ${planName} plan is now active.`;

  return (
    <ModalPortal>
      <div ref={dialog}
        className="fixed inset-0 z-50 bg-black/50 animate-fade-in motion-reduce:animate-none"
        role="dialog" aria-modal="true" aria-label="Subscription activated"
        aria-describedby="subscription-success-message subscription-success-hint"
        onClick={event => { if (event.target === event.currentTarget) close('backdrop'); }}
        // No operable control inside, so useModalA11y's trap finds nothing to
        // cycle: hold Tab here or focus leaves for the page underneath.
        onKeyDown={event => { if (event.key === 'Tab') event.preventDefault(); }}>
        <div className="pointer-events-none fixed left-1/2 top-[22vh] w-max max-w-[min(356px,calc(100vw-2rem))] -translate-x-1/2 max-[600px]:top-[18vh]">
          <SubscriptionSuccessCard ref={message} text={text} />
        </div>
        <p id="subscription-success-hint" className="sr-only">Press Escape or click outside this message to dismiss.</p>
      </div>
    </ModalPortal>
  );
}
